/**
 * SuiviDashboard — tableau de bord du suivi de prospection (toute l'équipe).
 * Source : GET /api/v2/suivi/liste ; export tableur : GET /api/v2/suivi/export.csv
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Chip, TextField, MenuItem, Button, CircularProgress, Alert, Grid, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { STATUTS, SuiviChip, directionsUrl, GROUPES, OCCUPANTS, PROJETS, formatChance } from './SuiviProspection';
const libelle = (liste, v) => (liste.find(o => o.value === v) || {}).label || v || '';

const API_BASE = process.env.REACT_APP_API_URL || '';

const columns = [
  { field: 'statut_chip', headerName: 'Statut', width: 175, sortable: false, renderCell: (p) => <SuiviChip suivi={p.row} /> },
  { field: 'groupe', headerName: 'Groupe', width: 95, renderCell: (p) => p.value && GROUPES[p.value] ? <Chip label={GROUPES[p.value].label} size="small" sx={{ bgcolor: GROUPES[p.value].bg, color: GROUPES[p.value].color, fontWeight: 700, height: 20, fontSize: '0.68rem' }} /> : '' },
  { field: 'decile', headerName: 'Décile', width: 110, renderCell: (p) => p.value ? `D${p.value}${p.row.score ? ' · ' + formatChance(p.row.score) : ''}` : '' },
  { field: 'adresse', headerName: 'Adresse', flex: 1, minWidth: 200 },
  { field: 'commune', headerName: 'Commune', width: 140 },
  { field: 'date_prospection', headerName: 'Prospecté le', width: 115, renderCell: (p) => p.value ? new Date(p.value).toLocaleDateString('fr-FR') : '—' },
  { field: 'date_rdv', headerName: 'Rendez-vous', width: 140, renderCell: (p) => p.value ? new Date(p.value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—' },
  { field: 'occupant', headerName: 'Occupant', width: 130, renderCell: (p) => <span style={{ fontSize: '0.78rem' }}>{libelle(OCCUPANTS, p.value)}</span> },
  { field: 'projet', headerName: 'Projet', width: 150, renderCell: (p) => <span style={{ fontSize: '0.78rem' }}>{libelle(PROJETS, p.value)}</span> },
  { field: 'relance_le', headerName: 'Relance', width: 100, renderCell: (p) => p.value ? new Date(p.value).toLocaleDateString('fr-FR') : '' },
  { field: 'contact_nom', headerName: 'Contact', width: 140 },
  { field: 'contact_tel', headerName: 'Téléphone', width: 120 },
  { field: 'notes', headerName: 'Notes', flex: 1.2, minWidth: 220, renderCell: (p) => <span style={{ fontSize: '0.78rem', whiteSpace: 'normal', lineHeight: 1.2 }}>{p.value || ''}</span> },
  { field: 'agent', headerName: 'Agent', width: 110, renderCell: (p) => (p.value || '').split('@')[0] },
  { field: 'updated_at', headerName: 'Mis à jour', width: 130, renderCell: (p) => p.value ? new Date(p.value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '' },
  {
    field: 'itineraire', headerName: '', width: 90, sortable: false,
    renderCell: (p) => { const u = directionsUrl(p.row); return u ? <Button size="small" href={u} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontSize: '0.72rem' }}>Itinéraire</Button> : null; },
  },
];

export default function SuiviDashboard() {
  const [cp, setCp] = useState('');
  const [statut, setStatut] = useState('');
  const [groupe, setGroupe] = useState('');
  const [jours, setJours] = useState(365);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ jours, limit: 2000 });
      if (cp.trim()) params.append('cp', cp.trim());
      if (statut) params.append('statut', statut);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/v2/suivi/liste?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 20000 });
      setData(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [cp, statut, jours]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.biens || []).filter(r => !groupe || (r.groupe || 'autre') === groupe).map(r => ({ ...r, id: `${r.source}:${r.cle}` }));
  const groupes = data?.groupes || {};
  const pct = (a, b) => b ? `${Math.round(100 * a / b)} %` : '—';
  const relances = (data?.biens || []).filter(r => r.relance_le && new Date(r.relance_le) <= new Date() && !['mandat', 'refus', 'sans_suite'].includes(r.statut));
  const stats = data?.stats || {};
  const total = Object.values(stats).reduce((a, b) => a + b, 0);
  const rdvAVenir = rows.filter(r => r.date_rdv && new Date(r.date_rdv) >= new Date()).sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));
  const exportUrl = `${API_BASE}/v2/suivi/export.csv?jours=${jours}${cp.trim() ? `&cp=${cp.trim()}` : ''}${statut ? `&statut=${statut}` : ''}`;
  void relances;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>📋 Suivi de prospection</Typography>
          <Typography variant="body2" color="text.secondary">Ce que l'équipe a saisi sur les biens : statut, dates, rendez-vous, notes. Partagé entre tous les comptes.</Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualiser</Button>
          <Button variant="contained" size="small" startIcon={<DownloadIcon />} href={exportUrl} target="_blank" rel="noopener noreferrer">Export tableur</Button>
        </Box>
      </Box>

      <Accordion sx={{ mb: 2, bgcolor: '#fffdf5', border: '1px solid #f3e7c3' }} elevation={0} defaultExpanded={!localStorage.getItem('suivi_consignes_vues')} onChange={(e, open) => { if (!open) try { localStorage.setItem('suivi_consignes_vues', '1'); } catch (err) {} }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={800}>📝 Comment remplir une fiche, et pourquoi</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <b>La règle : une fiche par porte frappée, remplie le jour même, y compris quand personne n'a ouvert.</b> Une porte sans fiche n'existe pas dans les statistiques,
            et une fiche remplie de mémoire une semaine plus tard vaut moins qu'une absence de fiche.
          </Typography>
          <Typography variant="body2" component="div" sx={{ '& li': { mb: 0.6 } }}>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li><b>Statut</b>, toujours. Prospecté = quelqu'un a ouvert et on a parlé. Absent = personne. Refus, rendez-vous, mandat, sans suite. Jamais « prospecté » sans avoir parlé à l'occupant.</li>
              <li><b>Qui occupe la maison</b> : propriétaire, locataire, résidence secondaire, vide. Trente secondes de conversation, une information que les données publiques n'ont pas.</li>
              <li><b>Projet de vente</b> : aucun, en réflexion, dans les 12 mois, déjà en vente, vendu. Le champ le plus précieux pour l'outil : l'intention avant tout DPE. Dans un an, on le croisera avec les ventes réelles.</li>
              <li><b>Notes, en un mot le motif</b> quand il est dit : succession, séparation, retraite, mutation, maison trop grande, travaux trop lourds, bailleur qui arrête. Un mot suffit.</li>
              <li><b>Relance</b> : date et ce qu'il faudra dire. Absent = relance à 15 jours proposée d'office. Les relances à faire s'affichent en haut de cette page.</li>
              <li><b>Contact et téléphone</b> seulement si la personne les donne en sachant qu'on la rappellera. Si elle refuse tout démarchage : statut refus, rien d'autre.</li>
            </ol>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            <b>Deux consignes de méthode.</b> Les maisons témoins (pastille 🎲) se prospectent exactement comme les autres, même jour, même discours, sans les sauter.
            On suit la liste dans l'ordre du classement à l'intérieur d'une commune, plutôt que de choisir les maisons qui « ont l'air » de vendre : c'est justement ce qu'on veut mesurer.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Ce que ça rapporte : relances datées, rendez-vous et historique partagés (pour vous) ; portes par rendez-vous et par mandat, cible contre témoin (pour la direction) ;
            après 500 portes environ, premières variables internes testables contre les ventes réelles (pour l'outil). À 100 portes par semaine, le tableau devient lisible en deux à trois mois.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Code postal" value={cp} onChange={e => setCp(e.target.value)} inputProps={{ maxLength: 5 }} /></Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth select size="small" label="Statut" value={statut} onChange={e => setStatut(e.target.value)}>
              <MenuItem value="">Tous</MenuItem>
              {STATUTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth select size="small" label="Groupe" value={groupe} onChange={e => setGroupe(e.target.value)}>
              <MenuItem value="">Tous</MenuItem>
              {Object.entries(GROUPES).map(([k, g]) => <MenuItem key={k} value={k}>{g.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth select size="small" label="Période" value={jours} onChange={e => setJours(Number(e.target.value))}>
              <MenuItem value={30}>30 jours</MenuItem><MenuItem value={90}>3 mois</MenuItem><MenuItem value={365}>12 mois</MenuItem><MenuItem value={3650}>Tout</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`${total} biens suivis`} color="primary" sx={{ fontWeight: 700 }} />
            {STATUTS.filter(s => stats[s.value]).map(s => (
              <Chip key={s.value} label={`${s.label} : ${stats[s.value]}`} onClick={() => setStatut(statut === s.value ? '' : s.value)}
                sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, border: statut === s.value ? `2px solid ${s.color}` : 'none' }} />
            ))}
            {rdvAVenir.length > 0 && <Chip label={`📅 ${rdvAVenir.length} rendez-vous à venir`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />}
            {relances.length > 0 && <Chip label={`🔔 ${relances.length} relance${relances.length > 1 ? 's' : ''} à faire`} sx={{ bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 700 }} />}
          </Box>
          {Object.keys(groupes).length > 0 && (
            <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Rendement par groupe (cible = déciles 1 à 3, témoin = tiré au sort hors cible)</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 640 }}>
                  <thead><tr style={{ background: '#f5f5f5' }}>{['Groupe', 'Biens', 'Visités', 'Portes ouvertes', 'Rendez-vous', 'Mandats', 'Projets de vente déclarés'].map(h => <th key={h} style={{ textAlign: 'left', padding: '4px 10px' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {['cible', 'temoin', 'autre'].filter(g => groupes[g]).map(g => { const r = groupes[g]; return (
                      <tr key={g} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ padding: '4px 10px' }}><Chip label={GROUPES[g].label} size="small" sx={{ bgcolor: GROUPES[g].bg, color: GROUPES[g].color, fontWeight: 700, height: 20, fontSize: '0.68rem' }} /></td>
                        <td style={{ padding: '4px 10px' }}>{r.n}</td><td style={{ padding: '4px 10px' }}>{r.visites}</td>
                        <td style={{ padding: '4px 10px' }}>{r.portes_ouvertes} ({pct(r.portes_ouvertes, r.visites)})</td>
                        <td style={{ padding: '4px 10px' }}>{r.rdv} ({pct(r.rdv, r.visites)})</td>
                        <td style={{ padding: '4px 10px' }}><b>{r.mandats}</b> ({pct(r.mandats, r.visites)})</td>
                        <td style={{ padding: '4px 10px' }}>{r.projets} ({pct(r.projets, r.visites)})</td>
                      </tr>); })}
                  </tbody>
                </table>
              </Box>
              <Typography variant="caption" color="text.secondary">Les taux sont calculés sur les biens visités. La comparaison cible / témoin n'a de sens qu'à effort comparable et sur plusieurs dizaines de visites par groupe.</Typography>
            </Paper>
          )}

          {rdvAVenir.length > 0 && (
            <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: '#fffaf3' }}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Prochains rendez-vous</Typography>
              {rdvAVenir.slice(0, 8).map(r => (
                <Typography key={r.id} variant="body2" sx={{ py: 0.3 }}>
                  <b>{new Date(r.date_rdv).toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</b>
                  {' · '}{r.adresse}{r.commune ? `, ${r.commune}` : ''}{r.contact_nom ? ` · ${r.contact_nom}` : ''}{r.contact_tel ? ` · ${r.contact_tel}` : ''}{r.agent ? ` · ${r.agent.split('@')[0]}` : ''}
                </Typography>
              ))}
            </Paper>
          )}

          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <DataGrid rows={rows} columns={columns} pageSize={50} rowsPerPageOptions={[50, 100, 200]} autoHeight disableSelectionOnClick
              getRowHeight={() => 'auto'} sx={{ border: 'none', '& .MuiDataGrid-cell': { py: 0.6 }, '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 700 } }} />
          </Paper>
        </>
      )}
    </Box>
  );
}
